import {
	COND_PAYINADVANCE,
	COND_PAYTHERE,
	DEFAULT_SWITCH_DAY,
	PER_FIXED,
	PER_PERSON,
	PER_PERSONWEEK,
	PER_PERSONWEEK_PRORATED,
	PER_WEEK,
} from 'app/_server/database/constants'
import {validators} from 'app/Components/Form'
import {calcDueDates, calcNumDays, fixDate, isDeepEmpty} from 'app/lib'
import {
	calcFirstAvailableDate,
	calcOptionPrice,
	calcPrices,
	deriveOptions,
} from 'app/lib/calcPrice'
import {add} from 'date-fns'
import {get, groupBy, isEmpty} from 'lodash-es'

const capacityExceededMsg = 'capacityExceeded'
const fieldIsRequiredMsg = 'required'

export const validate = (
	values: {
		guests: GuestsFormType
		dateRange: Contract['dateRange']
		personalDetails
	},
	props: {
		capacity: House['capacity']
		hasInsurance: Contract['hasInsurance']
	}
) => {
	const {guests, dateRange, personalDetails} = values || {}
	const {capacity, hasInsurance} = props
	const errors = {
		guests: {},
		personalDetails: {
			address: {},
			insurance: {},
		},
		// TODO as ReservationFormType
	} as any

	const adults = Number(guests?.adults) || 0
	const kids = Number(guests?.kids) || 0
	const guestsTotal = adults + kids

	let maxStep = 0

	if (dateRange?.start && dateRange.end) {
		maxStep = 1
	} else {
		errors.dateRange = fieldIsRequiredMsg
	}

	if (guests) {
		if (adults <= 0) errors.guests.adults = 'leastOneAdultReq'
		if (kids < 0) errors.guests.kids = fieldIsRequiredMsg
		if (guestsTotal > capacity) {
			errors.guests.adults = capacityExceededMsg
			if (kids) errors.guests.kids = capacityExceededMsg
		}
		if (maxStep === 1 && isEmpty(errors.guests)) maxStep = 2
	} else {
		errors.guests = {adults: fieldIsRequiredMsg}
	}

	if (personalDetails) {
		const {
			personalDetails: {
				firstName,
				lastName,
				address = {},
				email,
				insurance = {},
			},
		} = values

		if (!firstName) errors.personalDetails.firstName = fieldIsRequiredMsg
		if (!lastName) errors.personalDetails.lastName = fieldIsRequiredMsg
		if (!address.street)
			errors.personalDetails.address.street = fieldIsRequiredMsg
		if (!address.pcode)
			errors.personalDetails.address.pcode = fieldIsRequiredMsg
		if (!address.city) errors.personalDetails.address.city = fieldIsRequiredMsg
		if (!address.countryId)
			errors.personalDetails.address.countryId = fieldIsRequiredMsg

		if (hasInsurance) {
			// The amount of guest fields is dynamic, based on guestsTotal.
			for (let i = 0; i < guestsTotal; i++) {
				const key = `guest${i + 1}`
				errors.personalDetails.insurance[key] = {}
				if (i > 0) {
					if (!get(insurance, `[${key}].firstName`))
						errors.personalDetails.insurance[key].firstName = fieldIsRequiredMsg
					if (!get(insurance, `[${key}].lastName`))
						errors.personalDetails.insurance[key].lastName = fieldIsRequiredMsg
				}
				if (!get(insurance, `[${key}].birthYear`))
					errors.personalDetails.insurance[key].birthYear = fieldIsRequiredMsg
			}
		}

		errors.personalDetails.email = email
			? validators.email(email)
			: validators.required(email)

		if (maxStep === 2 && isDeepEmpty(errors.personalDetails)) maxStep = 3
	}

	return {
		reservationForm: {_error: {maxStep}},
		...errors,
	}
}

const makeExtrasWithGuestAndPetsCount = (
	extras: Extra[],
	guestsToCharge: Contract['adults'],
	pets: Contract['pets']
): Extra[] => {
	if (!extras?.length) return []

	const newExtras: Extra[] = []
	for (const e of extras) {
		const newE = {...e}
		if (
			e.required &&
			e.per &&
			[PER_PERSON, PER_PERSONWEEK, PER_PERSONWEEK_PRORATED].includes(e.per)
		)
			newE.count = guestsToCharge

		if (e.pet || e.optionId === 'xt$huisdier' || e.optionId === 'xt$dieren')
			newE.count = pets

		newExtras.push(newE)
	}

	return newExtras
}

const calcExtrasPrices = (
	houseExtras: Extra[],
	stayDays: number
): ExtrasPricesByCondition => {
	const prices = {} as ExtrasPricesByCondition
	for (const extra of houseExtras) {
		if (!(extra.required || extra.count)) continue
		const price = calcOptionPrice(extra, stayDays, false)
		// creating an object of 'condition: prices sum of this condition'
		if (price)
			prices[extra.condition] = {
				...prices[extra.condition],
				[extra.optionId]: price,
				extraTypeSum: (prices[extra.condition]?.extraTypeSum || 0) + price,
			}
	}
	return prices
}

export const makeContractCalcProps = ({
	chosenExtrasObj,
	dateRange,
	forcePrices,
	guests,
	hasInsurance,
	house,
	rentalFees,
	taxPlatform,
}: {
	chosenExtrasObj: ExtrasObj
	dateRange?: Contract['dateRange'] | DateISOStringRange
	forcePrices?: ContractPrices
	guests?: Partial<GuestsFormType>
	hasInsurance: Contract['hasInsurance']
	house: House
	rentalFees?: Contract['rentalFees']
	taxPlatform?: TouristTaxPlatform
}): ExtraContractPropsType['contractCalc'] => {
	const {adults = 0, kids = 0, babies = 0, pets = 0} = guests || {}
	const guestsTotal = adults + kids + babies
	const guestsToCharge = adults + kids || 2

	const firstAvailableDate = house
		? calcFirstAvailableDate(house.prices)
		: undefined

	const blockedRanges = house?.blockedRanges?.map(res => ({
		...res,
		type: 'BLOCK',
	}))

	const houseExtrasObj = Object.fromEntries(
		(house?.extras || []).map(e => [e.optionId, e])
	)

	const housePlusChosenExtrasObj = {} as ExtrasObj
	for (const optionId of [
		...Object.keys(houseExtrasObj),
		...Object.keys(chosenExtrasObj || {}),
	]) {
		housePlusChosenExtrasObj[optionId] = {
			...houseExtrasObj[optionId],
			...chosenExtrasObj[optionId],
		}
	}

	const allExtras =
		makeExtrasWithGuestAndPetsCount(
			Object.values(housePlusChosenExtrasObj),
			guestsToCharge,
			pets
		) || []

	const groupedExtrasByCond = groupBy(
		allExtras,
		'condition'
	) as ExtrasGroupedByCondition

	const requiredExtras = allExtras.filter(extra => extra.required)
	const mergedExtras = allExtras.map(e =>
		e.required
			? e
			: chosenExtrasObj[e.optionId]
				? {...e, ...chosenExtrasObj[e.optionId]}
				: e
	)
	const selectedExtras = deriveOptions({options: mergedExtras})

	const optionalextrasState = Object.fromEntries(
		mergedExtras.filter(e => !e.required && !e.pet).map(e => [e.optionId, e])
	)

	const hasDateRange = !!(dateRange?.start && dateRange.end)

	const prices =
		house && hasDateRange
			? ((forcePrices ||
					calcPrices(
						house,
						{
							beginDate: fixDate(dateRange.start),
							endDate: fixDate(dateRange.end),
							options: selectedExtras,
							rentalFees,
							hasInsurance,
							derived: true,
							adults,
							kids,
							babies,
							hasTouristTax: !!taxPlatform,
						},
						taxPlatform
					)) as ContractPrices)
			: undefined

	const {stayDays = 0} = prices || {}

	const extrasPrices = hasDateRange
		? calcExtrasPrices(mergedExtras, stayDays)
		: ({} as ExtrasPricesByCondition)

	const {payThere} = house || {}

	const dueDates =
		hasDateRange && prices
			? calcDueDates({
					contractDate: fixDate(new Date()),
					beginDate: fixDate(new Date(dateRange.start)),
					prices,
				})
			: []

	const deposit = dueDates?.find(d => d.isDeposit)
	const total = dueDates?.find(d => !d.isDeposit)
	const guarantee = dueDates?.find(d => d.isGuarantee)

	// payments prices & due dates
	const depositPayDate = deposit?.date
	const totalPayDate = total?.date
	// total + guarantee - deposit
	const sellTotalToPayBalance =
		((guarantee || total)?.amount || 0) - (deposit?.amount || 0)

	const fieldsFromContract = {
		prices,
		hasInsurance,
		rentalFees,
		hasTouristTax: !!taxPlatform,
	}

	const fieldsFromHouse = {
		payThere,
		switchDay: DEFAULT_SWITCH_DAY,
	}

	return {
		...fieldsFromContract,
		...fieldsFromHouse,
		blockedRanges,
		depositPayDate,
		totalPayDate,
		sellTotalToPayBalance,
		extrasPrices,
		firstAvailableDate,
		groupedExtrasByCond,
		guests,
		guestsToCharge,
		guestsTotal,
		optionalextrasState,
		requiredExtras,
		selectedExtras,
	}
}

/** Finds the first date range that matches the given date. */
export const findDateRange = (
	date: Date,
	ranges: Pick<DateRangeItem, 'beginDate' | 'endDate'>[]
) => {
	for (const range of ranges) {
		const {beginDate, endDate} = range
		if (beginDate && endDate) {
			const begin = new Date(beginDate)
			const end = new Date(endDate)
			if (date >= begin && date < end) {
				return range
			}
		}
	}
	return null
}

/** Finds the first sell price range that matches the given date. */
export const findPriceRange = (
	date: Date,
	prices: Pick<HousePrice, 'date' | 'sell'>[],
	needsForSale = false
): Pick<HousePrice, 'date' | 'sell'> | null => {
	const fixedDate = fixDate(date)
	let foundRange: Pick<HousePrice, 'date' | 'sell'> | null = null
	for (const price of prices) {
		const {date: priceDate} = price
		if (priceDate) {
			const begin = fixDate(priceDate)
			if (fixedDate >= begin || (!foundRange && needsForSale)) {
				foundRange = price.sell ? price : null
			} else {
				break
			}
		}
	}
	return foundRange
}

/** Checks if a date is bookable based on the prices and blocked ranges. */
const isDateBookable = (
	date: Date,
	prices: Pick<HousePrice, 'date' | 'sell'>[],
	blockedRanges: Pick<DateRangeItem, 'beginDate' | 'endDate'>[]
): boolean => {
	const priceRange = findPriceRange(date, prices)
	const blockedRange = findDateRange(date, blockedRanges)

	if (priceRange && !blockedRange) {
		return true
	}

	return false
}

/** Find first available date in prices ranges in calendar */
export const calcFirstBookableDate = (
	house: Pick<House, 'prices' | 'blockedRanges'>,
	forceInitDate?: Contract['beginDate']
) => {
	const {prices, blockedRanges} = house || {}

	let thisDate = forceInitDate ? new Date(forceInitDate) : new Date()
	let nextDate

	while (true) {
		nextDate = add(thisDate, {days: 1})
		// this day and the next day is free
		if (
			isDateBookable(thisDate, prices, blockedRanges) &&
			isDateBookable(nextDate, prices, blockedRanges)
		) {
			return thisDate
		}

		const nextPriceRange = findPriceRange(nextDate, prices)

		// No more prices available, break the loop
		if (!nextPriceRange || !nextPriceRange.sell) {
			break
		}

		thisDate = nextDate
	}

	throw new Error('No start date found')
}

/**
 * Extras characteristics
 *
 * - IsRealPer: true if the extra has a per property and it's not PER_FIXED
 * - IsBool: true if the extra has no per property or it's PER_FIXED
 * - IsPerPersonOnly: true if the extra has a per property and it's PER_PERSON
 * - IsPerWeekOnly: true if the extra has a per property and it's PER_WEEK
 * - IsPerPersonWeek: true if the extra has a per property and it's PER_PERSONWEEK
 * - IsPerPet: true if the extra has a pet property and it's not PER_FIXED
 * - IsPetBool: true if the extra has a pet property and it's PER_FIXED
 * - IsFixedWithPrice: true if the extra has a per property and it's PER_FIXED
 * - IsFixedWithoutPrice: true if the extra has a per property and it's PER_FIXED
 */
export const calcExtraTypes = (extra: Extra) => {
	const {price, per, pet} = extra

	const isRealPer = !!per && per !== PER_FIXED
	const isBool = !isRealPer || extra.max === 1

	const isPerPersonOnly = !!per && [PER_PERSON].includes(per)
	const isPerWeekOnly = !!per && [PER_WEEK].includes(per)
	const isPerPersonWeek =
		per && [PER_PERSONWEEK, PER_PERSONWEEK_PRORATED].includes(per)
	const isPerPet = !!pet && isRealPer
	const isPetBool = !!pet && isBool

	const isFixedWithPrice = per === PER_FIXED && !!price
	const isFixedWithoutPrice = per === PER_FIXED && !price

	return {
		isRealPer,
		isBool,
		isPerPersonOnly,
		isPerWeekOnly,
		isPerPersonWeek,
		isPerPet,
		isPetBool,
		isFixedWithPrice,
		isFixedWithoutPrice,
	}
}

/**
 * Normalizes the extras array by ensuring that each extra has a count,
 * selected, and required property.
 */
export const normalizeExtras = (extras: Extra[]): Extra[] => {
	const normalizedExtras: Extra[] = extras.map(extra => {
		const {optionId, condition, pet} = extra || {}
		if (!optionId) throw new Error('optionId is required')
		const isPayable =
			condition === COND_PAYINADVANCE || condition === COND_PAYTHERE
		const count = extra.required ? 1 : 0
		return {
			count,
			selected: false,
			required: false,
			...extra,
			...((!isPayable || !!pet) && {required: true}),
		}
	})
	return normalizedExtras
}

/**
 * Updates the extras based on the contract and selected values. Calculates the
 * number of people and weeks for the extras. Sets the count and selected
 * properties for each extra. Returns the updated extras array.
 */
export const updateOptions = (
	contract: Pick<
		Contract,
		'adults' | 'kids' | 'pets' | 'house' | 'beginDate' | 'endDate'
	>,
	selectableExtrasValues?: SelectableExtrasValues
): Extra[] => {
	const {
		adults = 0,
		kids = 0,
		pets = 0,
		house,
		beginDate,
		endDate,
	} = contract || {}
	const {extras: houseExtras} = house || {}
	const stayDays = calcNumDays(beginDate, endDate)
	const totalPeopleCount = adults + kids
	const totalWeeks = Math.ceil(stayDays / 7)

	const updatedExtras: Extra[] = houseExtras.map(extra => {
		const {
			isRealPer,
			isPerPersonOnly,
			isPerPet,
			isPerWeekOnly,
			isPerPersonWeek,
		} = calcExtraTypes(extra)
		const newExtra = {...extra}

		if (isRealPer) {
			if (isPerPersonOnly) newExtra.count = totalPeopleCount

			if (isPerWeekOnly) newExtra.count = totalWeeks

			if (isPerPersonWeek)
				newExtra.count =
					(PER_PERSONWEEK_PRORATED === extra.per! ? stayDays / 7 : totalWeeks) *
					totalPeopleCount

			if (isPerPet) newExtra.count = pets

			if (isPerPet && isPerWeekOnly) newExtra.count = totalWeeks * pets
		}

		// If the extra is a pet and the number of pets is 0, set selected to false
		if (extra.required && (!newExtra.pet || (newExtra.pet && pets > 0)))
			newExtra.selected = true

		return newExtra
	})

	if (selectableExtrasValues) {
		for (const [optionId, value] of Object.entries(selectableExtrasValues)) {
			const extra = updatedExtras.find(e => e.optionId === optionId)
			if (!extra) continue
			const {isRealPer, isBool} = calcExtraTypes(extra)
			if (isRealPer) {
				if (typeof value === 'boolean') {
					extra.selected = value
					extra.count = value ? extra.count : 0
				}
				if (typeof value === 'number') {
					extra.count = value
					extra.selected = value > 0
				}
			}
			if (isBool) {
				extra.selected = !!value
				extra.count = extra.selected ? 1 : 0
			}
		}
	}

	return updatedExtras
}

export const makeGuestsObj = (
	contract?: Pick<Contract, 'adults' | 'kids' | 'babies' | 'pets' | 'petsNote'>
): GuestsFormType => {
	const {
		adults = 2,
		kids = 0,
		babies = 0,
		pets = 0,
		petsNote = '',
	} = contract || {}
	return {
		adults,
		kids,
		babies,
		pets,
		petsNote,
	}
}
